import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { bubbleImageSrc, cargoMediaSrc } from '@/lib/bubbleImage';
import { Microphone, Stop, Sparkle, ArrowsClockwise, CurrencyDollar } from '@phosphor-icons/react';
import SceneComments from './SceneComments';
import MemoryCrystalButton from './MemoryCrystalButton';

const STICKERS = ['💛', '🫧', '✨', '🌊', '🔥', '⭐', '👀', '🌙'];

const BubbleHead = ({ b, side = 'left' }) => {
  const src = bubbleImageSrc(b);
  return (
    <motion.div
      initial={{ x: side === 'left' ? -120 : 120, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 60, damping: 14 }}
      className="flex flex-col items-center gap-1"
    >
      <div className="relative h-24 w-24 sm:h-32 sm:w-32 rounded-full overflow-hidden ring-2 ring-white/20">
        {src ? (
          <img src={src} alt={b?.name} className="h-full w-full object-cover breathe" />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-bx-cyan/40 to-bx-magenta/40" />
        )}
      </div>
      <div className="text-[11px] uppercase tracking-widest text-white/70 font-bold">{b?.name}</div>
    </motion.div>
  );
};

/**
 * Cinematic player for a Scene doc. Two bubbles meet, the carrier reveals
 * its cargo, the other bubble can leave a reaction.
 */
const EncounterScene = ({ scene, canReact, onReact }) => {
  const [stage, setStage] = useState('approach');     // approach -> reveal -> react -> closed
  const [audioB64, setAudioB64] = useState(null);
  const [recording, setRecording] = useState(false);
  const [submittingReact, setSubmittingReact] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    const t1 = setTimeout(() => setStage('reveal'), 1600);
    const t2 = setTimeout(() => setStage('react'), 4400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [scene?.id]);

  if (!scene) return null;
  const [bubbleA, bubbleB] = scene.bubbles;
  const carrier = scene.bubbles.find((b) => b.id === scene.carrier_bubble_id) || bubbleA;
  const other = scene.bubbles.find((b) => b.id !== carrier.id) || bubbleB;
  const cargo = scene.cargo;

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const b64 = await new Promise((res) => {
          const r = new FileReader();
          r.onload = () => res(String(r.result || '').split(',')[1] || '');
          r.readAsDataURL(blob);
        });
        setAudioB64(b64);
        stream.getTracks().forEach((t) => t.stop());
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
      setTimeout(() => { if (rec.state === 'recording') rec.stop(); setRecording(false); }, 20_000);
    } catch (err) { toast.error('mic permission denied'); }
  };

  const stopRec = () => {
    if (recRef.current?.state === 'recording') recRef.current.stop();
    setRecording(false);
  };

  const submitReaction = async (kind, sticker = null) => {
    if (submittingReact) return;
    setSubmittingReact(true);
    try {
      const body = {
        bubble_id: other.id,                   // reactor's bubble (the one being shown)
        kind,
        sticker,
        blob_b64: kind === 'voice' ? audioB64 : null,
      };
      const { data } = await api.post(`/scenes/${scene.id}/react`, body);
      toast.success('reaction sent');
      onReact?.(data.reaction);
      setStage('closed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not react');
    } finally { setSubmittingReact(false); }
  };

  return (
    <div data-testid="encounter-scene" className="rounded-3xl overflow-hidden relative bg-black/80 ring-1 ring-white/10">
      <div className="aspect-[16/10] sm:aspect-[16/8] relative bg-gradient-to-br from-[#10001a] via-[#000022] to-[#001a18]">
        {/* approach */}
        <div className="absolute inset-0 grid grid-cols-2 items-center justify-items-center px-6 sm:px-12 noise">
          <BubbleHead b={bubbleA} side="left" />
          <BubbleHead b={bubbleB} side="right" />
        </div>

        {/* reveal panel */}
        <AnimatePresence>
          {(stage === 'reveal' || stage === 'react') && cargo && (
            <motion.div
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
              className="absolute inset-x-4 bottom-4 glass rounded-2xl p-4 sm:p-5"
            >
              <div className="text-[10px] uppercase tracking-widest text-bx-cyan mb-1">
                {carrier.name} is carrying something
              </div>
              {cargo.kind === 'wish' && (
                <div className="font-display text-lg sm:text-2xl italic leading-snug">"{cargo.text}"</div>
              )}
              {cargo.kind === 'voice' && cargoMediaSrc(cargo) && (
                <audio controls autoPlay src={cargoMediaSrc(cargo)} className="w-full mt-1" />
              )}
              {cargo.kind === 'photo' && cargoMediaSrc(cargo) && (
                <img alt="" src={cargoMediaSrc(cargo)} className="rounded-xl max-h-44 w-full object-cover mt-1" />
              )}
              {cargo.kind === 'clip' && cargoMediaSrc(cargo) && (
                <video controls autoPlay playsInline src={cargoMediaSrc(cargo)}
                  className="rounded-xl max-h-64 w-full mt-1" />
              )}
              {cargo.commentary && (
                <div className="text-xs italic text-white/60 mt-2 border-l-2 border-bx-cyan/50 pl-3">
                  — {carrier.name}: {cargo.commentary}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* approach whisper */}
        {stage === 'approach' && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 0.8 }} exit={{ opacity: 0 }}
            className="absolute inset-x-0 top-4 text-center text-xs uppercase tracking-widest text-white/60"
          >
            two bubbles drift close…
          </motion.div>
        )}
      </div>

      {/* react controls */}
      {canReact && stage === 'react' && (
        <div data-testid="scene-react-tray" className="p-4 sm:p-5 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">
            {other.name}, leave something back
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {STICKERS.map((s) => (
              <button key={s}
                data-testid={`react-sticker-${s}`}
                onClick={() => submitReaction('sticker', s)}
                disabled={submittingReact}
                className="text-2xl hover:scale-125 transition px-2 py-1 rounded-full bg-white/5 hover:bg-white/10">
                {s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {!audioB64 && !recording && (
              <button data-testid="scene-rec-start" onClick={startRec}
                className="px-3 py-2 rounded-full bg-bx-magenta/20 hover:bg-bx-magenta/30 text-bx-magenta text-xs uppercase tracking-widest font-bold flex items-center gap-1.5">
                <Microphone size={14} weight="fill" /> record voice reply
              </button>
            )}
            {recording && (
              <button data-testid="scene-rec-stop" onClick={stopRec}
                className="px-3 py-2 rounded-full bg-red-500/20 text-red-300 text-xs uppercase tracking-widest font-bold flex items-center gap-1.5 animate-pulse">
                <Stop size={14} weight="fill" /> stop
              </button>
            )}
            {audioB64 && (
              <>
                <audio controls src={`data:audio/webm;base64,${audioB64}`} className="h-8" />
                <button onClick={() => submitReaction('voice')}
                  data-testid="scene-send-voice"
                  className="ml-auto btn-volt px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5">
                  <Sparkle size={14} weight="fill" /> send
                </button>
                <button onClick={() => setAudioB64(null)}
                  className="p-2 rounded-full hover:bg-white/10 text-white/65">
                  <ArrowsClockwise size={14} />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* existing reactions */}
      {(scene.reactions || []).length > 0 && (
        <div className="p-4 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">reactions</div>
          <div className="flex flex-wrap gap-2">
            {(scene.reactions || []).map((r) => (
              <div key={r.id} className="rounded-full bg-white/5 px-3 py-1 text-xs text-white/70 flex items-center gap-1.5">
                <span className="text-white/60">{r.bubble_name}:</span>
                {r.kind === 'sticker' ? <span className="text-base">{r.sticker}</span> :
                  r.kind === 'voice' && r.media_url ? (
                    <audio controls src={cargoMediaSrc(r)} className="h-6" />
                  ) : (
                    <span>echo</span>
                  )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tip jar */}
      <div className="border-t border-white/10 p-4 flex items-center gap-2 flex-wrap">
        <CurrencyDollar size={14} weight="duotone" className="text-bx-volt" />
        <span className="text-[10px] uppercase tracking-widest text-white/60 mr-1">tip the creator</span>
        {[1, 5, 20].map((amt) => (
          <button key={amt}
            data-testid={`tip-${amt}`}
            onClick={async () => {
              try {
                const { data } = await api.post(`/scenes/${scene.id}/tip/checkout`,
                  { scene_id: scene.id, amount_usd: amt, origin_url: window.location.href });
                window.location.href = data.checkout_url;
              } catch (err) { toast.error(err?.response?.data?.detail || 'could not'); }
            }}
            className="px-3 py-1 rounded-full bg-bx-volt/15 hover:bg-bx-volt/25 text-bx-volt text-[11px] font-bold">
            ${amt}
          </button>
        ))}
        {scene.tips_total_usd ? (
          <span className="ml-auto text-[10px] text-bx-gold">tipped · ${scene.tips_total_usd.toFixed(2)}</span>
        ) : null}
        <MemoryCrystalButton scene={scene} />
      </div>

      {/* Threaded comments */}
      <SceneComments scene={scene} />
    </div>
  );
};

export default EncounterScene;
